import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import EmotionalResonanceChart from '../echoes/EmotionalResonanceChart';

const SAGE = '#7C9A82';
const SEEN_KEY = 'mw.voiceTourSeen.v1';

/**
 * Four weeks of invented takes, for the last step's chart.
 *
 * Fed to the **real** EmotionalResonanceChart, not a drawing of one: a
 * hand-made mock is a promise the product may not keep, and it drifts the first
 * time the real chart changes. Real emotion codes, so it renders its real
 * labelled pills. Dates are relative so the axis never looks stale.
 */
const sampleEchoes = () => {
  const day = 24 * 60 * 60 * 1000;
  const base = Date.now() - 21 * day;
  const rows = [
    ['numb', 'relief'],
    ['overwhelmed', 'peaceful'],
    ['tired', 'relief'],
    ['lonely', 'glad'],
  ];
  return rows.map(([before, after], i) => ({
    id: `sample-${i}`,
    created_at: new Date(base + i * 7 * day).toISOString(),
    entry_date: new Date(base + i * 7 * day).toISOString().slice(0, 10),
    emotion_before: before,
    emotion_after: after,
    mood_before: null,
    mood_after: null,
    duration_ms: 90000,
    accepted_text: '',
  }));
};

const STEPS = [
  {
    title: 'Just talk',
    body: "Tap once and say what's on your mind. Stop when you're done — there is no time limit, and nothing to write.",
    anchor: 'record',
  },
  {
    title: "Name it, or don't",
    body: 'One tap if you know how you feel, "Not sure" if you don\'t. It is never required — it only makes the picture at the end richer.',
    anchor: null,
  },
  {
    title: 'It saves itself',
    body: "Every recording lands here, and in that day's journal entry. We write down what you said. There is no button to press.",
    anchor: 'card',
  },
  {
    title: 'Fix a word',
    body: 'If something came out wrong, open a recording and edit the text. It re-saves itself.',
    anchor: 'card',
  },
  {
    title: 'See the shift',
    body: 'Name how you feel before and after a few times, and this fills in with your own words.',
    anchor: 'strip',
    sampleChart: true,
  },
];

export const voiceTourSeen = () => {
  // A storage failure resolves to "seen": a tour that reruns on every visit is
  // worse than one a user misses once.
  try {
    return localStorage.getItem(SEEN_KEY) === 'true';
  } catch {
    return true;
  }
};

const markSeen = () => {
  try {
    localStorage.setItem(SEEN_KEY, 'true');
  } catch {
    /* private window, blocked storage — the tour simply runs again */
  }
};

/**
 * The Voice tour: a dimmed screen with a cut-out around the current step's
 * target and a card explaining it.
 *
 * Rendered only while running, so it costs nothing in ordinary use. The scrim
 * and the cut-out are **one absolutely-positioned div** with a 9999px spread
 * box-shadow — no SVG mask, no second layer, and nothing to repaint but a rect.
 *
 * It has to work on an **empty screen**: it runs the first time someone opens
 * Voice with nothing recorded, so the card and the strip it points at do not
 * exist yet. A missing anchor falls back to a centred card with a plain scrim,
 * which is the normal first-run case rather than an edge case.
 *
 * `anchors` maps a step's anchor name to a ref-like `{ current: Element }`.
 */
export default function VoiceTour({ open, anchors, onClose }) {
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const [measured, setMeasured] = useState(false);

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const finish = useCallback(() => {
    markSeen();
    onClose?.();
  }, [onClose]);

  const measure = useCallback(() => {
    const name = STEPS[step]?.anchor;
    const el = name ? anchors?.[name]?.current : null;
    if (!el) {
      setRect(null);
      setMeasured(true);
      return;
    }
    const r = el.getBoundingClientRect();
    // A zero-size box means the node exists but has not laid out yet; treat it
    // as absent rather than drawing a cut-out over the top-left corner.
    setRect(r.width && r.height ? r : null);
    setMeasured(true);
  }, [step, anchors]);

  // Layout effect, not effect: measuring after paint would show the card in
  // the wrong place for one frame.
  useLayoutEffect(() => {
    if (!open) return undefined;
    setMeasured(false);
    const id = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(id);
  }, [open, step, measure]);

  // Only while visible — no listeners left behind on a screen nobody is touring.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') finish();
      else if (e.key === 'ArrowRight') setStep((s) => Math.min(s + 1, STEPS.length - 1));
      else if (e.key === 'ArrowLeft') setStep((s) => Math.max(s - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, measure, finish]);

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  const chartRows = useMemo(() => (current?.sampleChart ? sampleEchoes() : null), [current]);

  if (!open || !measured) return null;

  const pad = 8;
  const cardWidth = 320;
  const margin = 16;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const cardMaxH = Math.min(current.sampleChart ? vh * 0.7 : vh * 0.5, 520);

  let top;
  let left;
  if (rect) {
    const below = vh - rect.bottom >= rect.top;
    top = below
      ? Math.min(rect.bottom + margin, vh - cardMaxH - margin)
      : Math.max(margin, rect.top - cardMaxH - margin);
    left = Math.min(
      Math.max(margin, rect.left + rect.width / 2 - cardWidth / 2),
      vw - cardWidth - margin
    );
  } else {
    top = Math.max(margin, (vh - cardMaxH) / 2);
    left = Math.max(margin, (vw - cardWidth) / 2);
  }

  return (
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="Voice tour">
      {rect ? (
        // One element: the scrim IS the box-shadow around the cut-out.
        <div
          aria-hidden="true"
          className="absolute rounded-2xl pointer-events-none transition-all duration-300 ease-out motion-reduce:transition-none"
          style={{
            top: rect.top - pad,
            left: rect.left - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
            boxShadow: `0 0 0 9999px rgba(41,37,36,0.55), 0 0 0 2px ${SAGE}`,
          }}
        />
      ) : (
        <div aria-hidden="true" className="absolute inset-0" style={{ backgroundColor: 'rgba(41,37,36,0.55)' }} />
      )}

      {/* Absorbs clicks so the screen underneath is inert during the tour. */}
      <div className="absolute inset-0" onClick={(e) => e.stopPropagation()} />

      <div
        className="absolute bg-white dark:bg-[#2D2A27] rounded-2xl shadow-xl p-5 overflow-y-auto"
        style={{ top, left, width: cardWidth, maxHeight: cardMaxH }}
      >
        <div className="flex items-start justify-between gap-3 mb-1.5">
          <span
            className="text-[11px] font-bold uppercase tracking-[0.12em]"
            style={{ color: SAGE }}
          >
            Step {step + 1} of {STEPS.length}
          </span>
          <button
            type="button"
            onClick={finish}
            aria-label="Skip tour"
            className="p-1 -m-1 rounded-full text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 transition-colors duration-300"
          >
            <X size={16} />
          </button>
        </div>

        <h3 className="text-base font-semibold text-stone-800 dark:text-[#F0EDE8]">
          {current.title}
        </h3>
        <p className="mt-1.5 text-sm leading-relaxed text-stone-500 dark:text-stone-400">
          {current.body}
        </p>

        {chartRows && (
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-xs font-semibold text-stone-600 dark:text-stone-300">
                Before &amp; after
              </span>
              <span className="text-[9px] font-bold tracking-[0.1em] uppercase px-1.5 py-0.5 rounded-full bg-stone-100 dark:bg-stone-700 text-stone-500 dark:text-stone-400">
                Example
              </span>
            </div>
            <EmotionalResonanceChart echoes={chartRows} />
          </div>
        )}

        <div className="flex items-center gap-3 mt-5">
          {step > 0 && (
            <button
              type="button"
              onClick={() => setStep((s) => s - 1)}
              className="text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 transition-colors duration-300"
            >
              Back
            </button>
          )}
          <button
            type="button"
            onClick={() => (isLast ? finish() : setStep((s) => s + 1))}
            className="ml-auto rounded-full px-5 py-2 text-sm font-semibold text-white transition-transform duration-300 ease-out active:scale-95 motion-reduce:transition-none"
            style={{ backgroundColor: SAGE }}
          >
            {isLast ? 'Got it' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
